/**
 * Hook personalizado para gestión de roles
 * Sistema Klever Octopus - Betel Natural
 */
import { useState, useEffect, useCallback } from 'react';
import rolesService from '../services/rolesService';
import { useDebounce } from './useDebounce';

export const useRoles = (initialParams = {}) => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20
  });

  // Parámetros de filtrado
  const [filters, setFilters] = useState({
    search: '',
    level: '',
    is_system: '',
    sort_by: 'name',
    sort_order: 'asc',
    ...initialParams
  });

  // Debounce para búsqueda
  const debouncedSearch = useDebounce(filters.search, 500);

  // Cargar roles
  const loadRoles = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);

    try {
      const queryParams = {
        ...filters,
        search: debouncedSearch,
        page: pagination.currentPage,
        per_page: pagination.itemsPerPage,
        ...params
      };

      const response = await rolesService.getRoles(queryParams);

      // Debug: Verificar respuesta del backend
      console.log('=== DEBUG useRoles - Response ===');
      console.log('Response completo:', response);
      console.log('Response.data:', response.data);
      console.log('Response.data.roles:', response.data?.roles);

      if (response.success) {
        // El backend retorna {total, roles}, no {data: []}
        const rolesData = response.data.roles || response.data || [];
        const total = response.data.total || rolesData.length;

        console.log('Roles data extraído:', rolesData);
        console.log('Total:', total);

        setRoles(rolesData);
        if (response.pagination) {
          setPagination(prev => ({
            ...prev,
            currentPage: response.pagination.page || 1,
            totalPages: response.pagination.pages || 1,
            totalItems: response.pagination.total || total,
            itemsPerPage: response.pagination.per_page || 20
          }));
        } else {
          // Si no hay pagination, usar total del response
          setPagination(prev => ({
            ...prev,
            totalItems: total
          }));
        }
      } else {
        setError(response.message);
        setRoles([]);
      }
    } catch (error) {
      console.error('Error loading roles:', error);
      setError('Error cargando roles');
      setRoles([]);
    } finally {
      setLoading(false);
    }
  }, [filters, debouncedSearch, pagination.currentPage, pagination.itemsPerPage]);

  // Crear rol
  const createRole = async (roleData) => {
    setLoading(true);
    setError(null);

    try {
      const response = await rolesService.createRole(roleData);

      if (response.success) {
        await loadRoles(); // Recargar lista
        return response;
      } else {
        setError(response.message);
        return response;
      }
    } catch (error) {
      console.error('Error creating role:', error);
      const errorResponse = { success: false, message: 'Error creando rol' };
      setError(errorResponse.message);
      return errorResponse;
    } finally {
      setLoading(false);
    }
  };

  // Actualizar rol
  const updateRole = async (id, roleData) => {
    setLoading(true);
    setError(null);

    try {
      const response = await rolesService.updateRole(id, roleData);

      if (response.success) {
        // Actualizar rol en la lista local usando value o id
        setRoles(prev => prev.map(role => {
          const roleId = role.value || role.id;
          return roleId === id ? { ...role, ...response.data } : role;
        }));
        return response;
      } else {
        setError(response.message);
        return response;
      }
    } catch (error) {
      console.error('Error updating role:', error);
      const errorResponse = { success: false, message: 'Error actualizando rol' };
      setError(errorResponse.message);
      return errorResponse;
    } finally {
      setLoading(false);
    }
  };

  // Eliminar rol
  const deleteRole = async (id) => {
    setLoading(true);
    setError(null);

    console.log('deleteRole llamado con ID:', id);

    try {
      const response = await rolesService.deleteRole(id);

      console.log('Respuesta de deleteRole:', response);

      if (response.success) {
        // Remover rol de la lista local usando value o id
        setRoles(prev => prev.filter(role => {
          const roleId = role.value || role.id;
          return roleId !== id;
        }));
        return response;
      } else {
        setError(response.message);
        return response;
      }
    } catch (error) {
      console.error('Error deleting role:', error);
      const errorResponse = { success: false, message: 'Error eliminando rol' };
      setError(errorResponse.message);
      return errorResponse;
    } finally {
      setLoading(false);
    }
  };

  // Asignar permisos a rol
  const assignPermissions = async (roleId, permissionIds) => {
    setLoading(true);
    setError(null);

    try {
      const response = await rolesService.assignPermissions(roleId, permissionIds);

      if (response.success) {
        // Actualizar rol en la lista local usando value o id
        setRoles(prev => prev.map(role => {
          const currentRoleId = role.value || role.id;
          return currentRoleId === roleId ? { ...role, permissions: response.data.permissions } : role;
        }));
        return response;
      } else {
        setError(response.message);
        return response;
      }
    } catch (error) {
      console.error('Error assigning permissions:', error);
      const errorResponse = { success: false, message: 'Error asignando permisos' };
      setError(errorResponse.message);
      return errorResponse;
    } finally {
      setLoading(false);
    }
  };

  // Obtener permisos de un rol
  const getRolePermissions = async (roleId) => {
    try {
      const response = await rolesService.getRolePermissions(roleId);
      return response;
    } catch (error) {
      console.error('Error getting role permissions:', error);
      return { success: false, message: 'Error obteniendo permisos del rol' };
    }
  };

  // Actualizar filtros
  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters
    }));
    // Resetear a la primera página cuando se cambian los filtros
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
  }, []);

  // Cambiar página
  const changePage = useCallback((page) => {
    setPagination(prev => ({
      ...prev,
      currentPage: page
    }));
  }, []);

  // Cambiar items por página
  const changeItemsPerPage = useCallback((itemsPerPage) => {
    setPagination(prev => ({
      ...prev,
      itemsPerPage,
      currentPage: 1 // Resetear a la primera página
    }));
  }, []);

  // Limpiar filtros
  const clearFilters = useCallback(() => {
    setFilters({
      search: '',
      level: '',
      is_system: '',
      sort_by: 'name',
      sort_order: 'asc'
    });
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
  }, []);

  // Efectos
  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  return {
    // Estado
    roles,
    loading,
    error,
    pagination,
    filters,

    // Acciones
    loadRoles,
    createRole,
    updateRole,
    deleteRole,
    assignPermissions,
    getRolePermissions,
    updateFilters,
    changePage,
    changeItemsPerPage,
    clearFilters,
    
    // Utilidades
    setError
  };
};

/**
 * Hook para obtener un rol específico
 */
export const useRole = (roleId) => {
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadRole = useCallback(async () => {
    if (!roleId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await rolesService.getRoleById(roleId);

      if (response.success) {
        setRole(response.data);
      } else {
        setError(response.message);
        setRole(null);
      }
    } catch (error) {
      console.error('Error loading role:', error);
      setError('Error cargando rol');
      setRole(null);
    } finally {
      setLoading(false);
    }
  }, [roleId]);

  useEffect(() => {
    loadRole();
  }, [loadRole]);

  return {
    role,
    loading,
    error,
    loadRole,
    setRole
  };
};

/**
 * Hook para obtener todos los roles (sin paginación)
 */
export const useAllRoles = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadAllRoles = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await rolesService.getAllRoles();

      console.log('=== useAllRoles - Response ===');
      console.log('Response:', response);

      if (response.success) {
        // El backend ahora retorna {total, roles} en lugar de un array directo
        let rolesData = response.data.roles || response.data || [];

        console.log('Roles data extraído:', rolesData);

        // Normalizar roles según el formato de la API
        if (Array.isArray(rolesData) && rolesData.length > 0) {
          // Si tienen el formato {value, name, description} (enum viejo)
          if (rolesData[0].value && rolesData[0].name && !rolesData[0].id) {
            rolesData = rolesData.map(role => ({
              id: role.value,
              name: role.value,
              display_name: role.name,
              description: role.description
            }));
          }
          // Si son strings simples
          else if (typeof rolesData[0] === 'string') {
            rolesData = rolesData.map(roleName => ({
              id: roleName,
              name: roleName,
              display_name: roleName.charAt(0).toUpperCase() + roleName.slice(1)
            }));
          }
          // Si ya tienen el formato correcto con id, dejarlo como está
        }

        console.log('Roles después de normalizar:', rolesData);
        setRoles(rolesData);
      } else {
        setError(response.message);
        setRoles([]);
      }
    } catch (error) {
      console.error('Error loading all roles:', error);
      setError('Error cargando roles');
      setRoles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAllRoles();
  }, [loadAllRoles]);

  return {
    roles,
    loading,
    error,
    loadAllRoles
  };
};

/**
 * Hook para estadísticas de roles
 */
export const useRoleStats = () => {
  const [stats, setStats] = useState({
    total: 0,
    system: 0,
    custom: 0,
    byLevel: {},
    usersCount: {}
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadStats = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await rolesService.getRoleStats();

      if (response.success) {
        setStats(response.data || {
          total: 0,
          system: 0,
          custom: 0,
          byLevel: {},
          usersCount: {}
        });
      } else {
        setError(response.message);
      }
    } catch (error) {
      console.error('Error loading role stats:', error);
      setError('Error cargando estadísticas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  return {
    stats,
    loading,
    error,
    loadStats
  };
};

/**
 * Utilidades para roles
 */
export const getRoleLevelColor = (level) => {
  const colors = {
    basic: '#28a745',      // Verde
    intermediate: '#ffc107', // Amarillo
    advanced: '#fd7e14',   // Naranja
    admin: '#dc3545',      // Rojo
    superadmin: '#6f42c1'  // Púrpura
  };
  
  return colors[level] || '#6c757d'; // Gris por defecto
};

export const getRoleLevelText = (level) => {
  const texts = {
    basic: 'Básico',
    intermediate: 'Intermedio', 
    advanced: 'Avanzado',
    admin: 'Administrador',
    superadmin: 'Super Admin'
  };
  
  return texts[level] || 'Sin definir';
};

export const canEditRole = (role, currentUserRole) => {
  // No se pueden editar roles del sistema
  if (role.is_system) return false;
  
  // Solo admin y superadmin pueden editar roles
  if (!['admin', 'superadmin'].includes(currentUserRole)) return false;
  
  // Superadmin puede editar cualquier rol
  if (currentUserRole === 'superadmin') return true;
  
  // Admin puede editar solo roles básicos e intermedios
  if (currentUserRole === 'admin') {
    return ['basic', 'intermediate'].includes(role.level);
  }
  
  return false;
};

export const canDeleteRole = (role, currentUserRole) => {
  // No se pueden eliminar roles del sistema
  if (role.is_system) return false;
  
  // No se puede eliminar un rol que tiene usuarios asignados
  if (role.users_count > 0) return false;
  
  // Solo superadmin puede eliminar roles
  return currentUserRole === 'superadmin';
};

export default useRoles;
